import { BadgeCard, ContextMenu } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, MouseEvent } from 'react';
import { useTeachingBlockCardMenu } from '../../../hooks/useTeachingBlockCardMenu';
import { getBadgeCardColors } from '../../../utils/badgeCardColors';
import { isBadgeCardWidth } from '../../../utils/typeguards';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { TeachingBlockCardType } from '../types';
import styles from './TeachingBlockCard.module.scss';

export interface TeachingBlockCardProps {
  card: TeachingBlockCardType;
  passive: boolean;
  duration?: number;
  count?: number;
  onHover?: (isHovering: boolean, card: TeachingBlockCardType) => void;
  onClick?: (event: MouseEvent<HTMLDivElement>, card: TeachingBlockCardType) => void;
  className?: string | undefined;
  /** Unterdrückt den nativen `title` – z.B. wenn ein externer Tooltip (ui-components) genutzt wird. */
  hideTitle?: boolean;
}

export const TeachingBlockCard: FC<TeachingBlockCardProps> = observer(
  ({ card, count = 1, duration, passive, onHover, onClick, className, hideTitle }) => {
    const store = useTeachingBlockStore();

    const realDuration = duration ? Math.ceil(duration / 7) : (card.duration ?? 1);

    // Darstellung „Fachfarbe für Kärtchen" – wie im Board, damit Stapel und Board zusammenpassen.
    const badgeColors = getBadgeCardColors({
      colorBySubject: store.cardColorBySubject,
      subjectColor: card.subject?.timetableConfig?.color ?? null,
      rows: card.badgeCardRows,
      teacherTextColor: card.badgeCardTextColor,
    });

    const { menu } = useTeachingBlockCardMenu({
      card,
      styles,
      hasWarnings: false,
      hasAvailabilityWarnings: false,
      isPicked: store.pickedCard?.card === card,
      isPinned: !!card.pinned,
    });

    const wrapperClasses = classNames(
      styles['teaching-block-card'],
      {
        [styles['is-passive']]: passive,
        [styles['is-multi']]: count >= 1,
        [styles['is-picked']]: store.pickedCard?.card.uuid === card.uuid,
        [styles['is-pinned']]: card.pinned,
        [styles['is-locked']]: card.locked,
        [styles['has-conflict']]:
          store.highlightMode.includes('warnings') &&
          ((card.availabilityWarnings && card.availabilityWarnings.length > 0) ||
            store.getWarningsForCard(card.uuid).length > 0),
      },
      className,
    );

    return (
      <>
        <ContextMenu className={styles.menu} data={menu} parent={document.getElementById('teaching-block-grid')}>
          <div
            className={wrapperClasses}
            title={hideTitle ? undefined : card.label}
            onClick={(e) => {
              !card.loading && onClick && onClick(e, card);
            }}
            onMouseEnter={() => onHover && onHover(true, card)}
            onMouseLeave={() => onHover && onHover(false, card)}
          >
            <BadgeCard
              count={count}
              color={badgeColors.color}
              backgroundColor={badgeColors.backgroundColor}
              width={isBadgeCardWidth(realDuration) ? realDuration : 6}
              label={card.subject?.shortName}
              rows={badgeColors.rows}
              showHover
              style={badgeColors.borderColor ? { outline: `1px solid ${badgeColors.borderColor}` } : undefined}
            />
          </div>
        </ContextMenu>
      </>
    );
  },
);
